import { PropsWithChildren, createContext, useContext, useEffect, useReducer } from 'react'
import { AreaSelected, InfoUser, UserAuth, authReducer } from './AuthReducer'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { removeApiAuthHeader, setApiAuthHeader, updateApiBaseUrl } from '../../api/aquaApi'
import { useQueryClient } from '@tanstack/react-query'
import { ContextProps, HandleLogin } from './types'
import { UserResponse } from '../../modules/Login/hooks/types'
import { useCheckSession } from '../../hooks/useCheckSession'
import { ConfigContext } from '../Config/ConfigContext'

export const AuthContext = createContext({} as ContextProps)

const initialState: InfoUser = {
  lastUserLogged: null,
  users: {},
  getTokenUrl: true,
  tokenType: 'normal',
}

const INIT_USER: UserAuth = {
  userId: null,
  userName: null,
  status: 'not-authorized',
  areaSelected: null,
  password: null,
  email: null,
  token: null,
  assignedAreas: null,
  permissions: null,
  remember: false,
  tokenType: 'normal',
}

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const queryClient = useQueryClient()
  const { storeConfig, clearConfig } = useContext(ConfigContext)

  useEffect(() => {
    checkRegisteredUser()
  }, [])

  useEffect(() => {
    const saveStorage = async () => {
      await AsyncStorage.setItem('aqua-users', JSON.stringify(state))
    }

    saveStorage()
  }, [state])

  const handleLogOut = async () => {
    stopInterval()
    dispatch({
      type: 'logOut',
    })
    removeApiAuthHeader()

    queryClient.clear()

    clearConfig()
  }

  const { startCountdown, stopInterval } = useCheckSession({ handleLogOut })

  const checkRegisteredUser = async () => {
    const users = await AsyncStorage.getItem('aqua-users')
    const infoUser: InfoUser = (users && JSON.parse(users)) || null

    const user = infoUser?.users[infoUser.lastUserLogged!.email]

    if (!user) {
      dispatch({
        type: 'logOut',
      })

      return
    }

    dispatch({
      type: 'logIn',
      payload: user,
    })
    setApiAuthHeader(user?.token ?? '')
  }

  const changeURLToken = async (value: boolean) => {
    dispatch({
      type: 'setTokenUrlWeb',
      payload: { urlTokenSetted: value },
    })
  }

  const handleLogIn = async ({ user, token, pass, remember, tokenType }: HandleLogin) => {
    const payload: UserAuth = {
      userId: user.id_user,
      userName: `${user.name} ${user.lastname}`,
      email: user.email,
      status: 'authorized',
      token,
      password: pass,
      areaSelected: handleAreaSaved(user),
      assignedAreas: handleAssignedArea(user),
      permissions: getPermissionByClient(user),
      remember,
      tokenType,
    }
    dispatch({
      type: 'logIn',
      payload,
    })

    setApiAuthHeader(token)
    startCountdown()

    try {
      if (!user.config?.config) {
        throw new Error('La respuesta de /me no incluye configuración')
      }

      await storeConfig(user.config.config)
      updateApiBaseUrl(user.config.config.apiUrls.app)
    } catch (error) {
      console.error('Error al procesar configuración desde /me:', error)
      throw new Error('No se pudo cargar la configuración del servidor. Verifica tu conexión.')
    }
  }

  const getPermissionByClient = (user: UserResponse) =>
    user.authorization?.permissions.filter((item) => item.client === 'app') ?? null

  const handleAreaSaved = (user: UserResponse): AreaSelected => {
    return handleAssignedArea(user) > 1
      ? state.users[user.email]?.status === 'authorized'
        ? state.users[user.email]?.areaSelected ?? null
        : null
      : resolveArea(getPermissionByClient(user).map((permiso) => permiso.path.split('/')[0]))
  }

  const resolveArea = (permission: (string | undefined)[]): AreaSelected => {
    if (permission.includes('sweetWater')) return 'sweetWater'
    if (permission.includes('seaWater')) return 'seaWater'
    if (permission.includes('health')) return 'health'
    return null
  }

  const handleAssignedArea = (user: UserResponse): number => {
    const arrayAreas = getPermissionByClient(user).map((permiso) => permiso.path.split('/')[0])
    const areas = new Set(arrayAreas)
    return areas.size
  }

  const handleAreaSelected = async (areaSelected: AreaSelected) => {
    dispatch({
      type: 'areaSelected',
      payload: areaSelected,
    })
    await AsyncStorage.setItem(
      'aqua-user',
      JSON.stringify({ ...state.users[state.lastUserLogged!.email], areaSelected }),
    )
  }

  return (
    <AuthContext.Provider
      value={{
        user: state.users[state.lastUserLogged?.email ?? ''] ?? INIT_USER,
        handleLogIn,
        handleLogOut,
        handleAreaSelected,
        lastUserLogged: state.lastUserLogged,
        getTokenUrl: state.getTokenUrl,
        changeURLToken,
        tokenType: state.tokenType,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
